import type { WhatsappIntegration, WhatsappProviderSummary } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorReason } from '../../../policies/api-error-reason.policy';
import { toConfigPayload, type ConfigFormValues } from '../../../policies/config-form-values.policy';
import { changesCredentials } from '../../../policies/messaging-provider.policy';
import { checkWhatsappIntegration, getWhatsappIntegration, installWhatsappIntegration, updateWhatsappIntegration } from '../whatsapp.api';

export interface WhatsappFormValues extends ConfigFormValues {
  displayName: string;
}

interface UseWhatsappIntegrationSheetArgs {
  providers: WhatsappProviderSummary[];
  /** The provider being installed (`?provider=`), or undefined for settings. */
  providerKey: string | undefined;
  /** The installation whose settings are open, or undefined when installing. */
  integrationId: string | undefined;
  onDone: () => void;
}

type SavedState = { status: 'none' } | { status: 'loading' } | { status: 'loaded'; integration: WhatsappIntegration } | { status: 'error' };

/**
 * Install a provider or change its settings. The API checks new credentials with the provider and saves nothing if it
 * refuses them, so the refusal shows inline (CLAUDE.md → Feedback).
 */
export function useWhatsappIntegrationSheet({ providers, providerKey, integrationId, onDone }: UseWhatsappIntegrationSheetArgs) {
  const toast = useToast();
  const [saved, setSaved] = useState<SavedState>(integrationId ? { status: 'loading' } : { status: 'none' });
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!integrationId) return;
    let cancelled = false;
    getWhatsappIntegration(integrationId)
      .then((integration) => !cancelled && setSaved({ status: 'loaded', integration }))
      .catch(() => !cancelled && setSaved({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  const integration = saved.status === 'loaded' ? saved.integration : undefined;
  const wantedKey = providerKey ?? integration?.providerKey;
  const provider = providers.find((candidate) => candidate.key === wantedKey);

  async function save(values: WhatsappFormValues): Promise<void> {
    if (!provider) return;
    setSubmitting(true);
    setError(null);
    const config = toConfigPayload(provider.fields, values);
    try {
      if (integration) {
        const isChecked = changesCredentials(provider.fields, integration, values);
        await updateWhatsappIntegration(integration.id, { displayName: values.displayName, ...(isChecked ? { config } : {}) });
        toast.success(`${values.displayName} saved`);
      } else {
        await installWhatsappIntegration({ providerKey: provider.key, displayName: values.displayName, config });
        toast.success(`${values.displayName} installed and sending WhatsApp messages`);
      }
      onDone();
    } catch (err) {
      setError(apiErrorReason(err, 'Could not save. Check the fields and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  /** Settings only: asks the provider whether it still accepts the saved details. */
  async function check(): Promise<void> {
    if (!integration) return;
    setChecking(true);
    setError(null);
    try {
      setSaved({ status: 'loaded', integration: await checkWhatsappIntegration(integration.id) });
      toast.success(`${integration.displayName} connection checked`);
    } catch (err) {
      setError(apiErrorReason(err, 'The connection could not be checked'));
    } finally {
      setChecking(false);
    }
  }

  return { provider, integration, isLoading: saved.status === 'loading', submitting, checking, error, save, check };
}
